import React, { useCallback } from 'react';

import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export type SplitButtonItem = {
  label: string;
  icon?: string;
  command?: () => void;
  disabled?: boolean;
  title?: string;
};

export type SplitButtonProps = {
  label: string;
  icon?: string;
  items: SplitButtonItem[];
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'xs' | 'sm' | 'lg';
  className?: string;
};

export const SplitButton = ({
  label,
  icon,
  items,
  onClick,
  disabled,
  variant = 'outline',
  size = 'sm',
  className,
}: SplitButtonProps) => {
  const handlePrimaryClick = useCallback(() => {
    if (!disabled && onClick) {
      onClick();
    }
  }, [disabled, onClick]);

  return (
    <div className={cn('inline-flex items-center', className)}>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={handlePrimaryClick}
        className="rounded-r-none border-r-0"
      >
        {icon && <i className={`${icon} text-xs`} data-icon="inline-start" />}
        {label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant={variant}
              size={size}
              disabled={disabled}
              className="rounded-l-none px-1.5"
            >
              <ChevronDown className="size-3" />
            </Button>
          }
        />
        <DropdownMenuContent align="end">
          {items.map((item) => (
            <DropdownMenuItem
              key={item.label}
              disabled={item.disabled}
              onClick={item.command}
            >
              {item.icon && <i className={`${item.icon} text-xs`} />}
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
